import React, { Component } from 'react';
import PropTypes from 'prop-types';
import WebSocket from 'reconnecting-websocket';

import { InvalidSignatureError } from '../errors';

const SocketContext = React.createContext();

export class SocketProvider extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: null,
      ErrorClass: Error,
      socketsConnected: {},
    };
    this.sockets = {};
    this.createSockets();
  }

  componentWillUnmount() {
    Object.keys(this.sockets).forEach((name) => {
      const socket = this.sockets[name];
      socket.removeEventListener('message', this.handleSocketMessage);
    });
  }

  createSockets = () => {
    const { sockets } = this.props;
    Object.keys(sockets).forEach((name) => {
      const url = sockets[name];
      this.createSocket(name, url);
    });
  };

  handleSocketMessage = (evt) => {
    let message = {};
    try {
      message = typeof evt.data === 'string' ? JSON.parse(evt.data) : evt.data;
    } catch (err) {
      console.error('failed to parse', evt.data);
      if (window.Rollbar && window.Rollbar.warning) {
        window.Rollbar.warning(evt.data);
      }
    }
    if (message.statusCode === 401) {
      this.setState({
        error: message.message,
        ErrorClass: InvalidSignatureError,
      });
    }
  };

  createSocket = (name, url) => {
    const { secure } = this.props;
    let protocol = 'wss:';
    if (!secure) {
      protocol = 'ws:';
    }
    const socket = new WebSocket(`${protocol}${url}`, null, {
      minReconnectionDelay: 1,
    });

    socket.addEventListener('message', this.handleSocketMessage);

    socket.addEventListener('error', (evt) => {
      // This was being sent 400k times monthly, because a basic timeout counts as a socket error.
      // We turned off tracking because the events didn't contain any useful debugging information
      // and were sent too often.
      // See occurrences of https://rollbar.com/minervaproject/SLE/items/889/
      // if (window.Rollbar && window.Rollbar.error && evt.message) {
      //   if (evt.message && evt.message !== 'TIMEOUT') {
      //     window.Rollbar.error('Websocket error', {
      //       event: evt,
      //     });
      //   }
      // }
    });

    socket.addEventListener('close', (evt) => {
      const { socketsConnected } = this.state;
      socketsConnected[name] = false;
      this.setState({ socketsConnected });

      console.log('[SocketProvider] WebSocket closed', {
        name,
        wasClean: evt.wasClean,
        code: evt.code,
        reason: evt.reason,
        timestamp: new Date().toISOString(),
      });

      // This was being sent a million times monthly as of April 3, 2023, because a basic timeout
      // counts as a socket error. We turned off tracking because the events didn't contain any useful debugging information
      // and were sent too often.
      // See occurrences of https://rollbar.com/minervaproject/SLE/items/890/
      // if (evt.wasClean === false) {
      //   window.Rollbar.warning('Websocket closed with wasClean false', {
      //     event: evt,
      //   });
      // }
    });

    socket.addEventListener('open', () => {
      const { socketsConnected } = this.state;
      socketsConnected[name] = true;
      this.setState({ socketsConnected });

      console.log('[SocketProvider] WebSocket opened', {
        name,
        timestamp: new Date().toISOString(),
      });
    });

    this.sockets[name] = socket;
  };

  getContext = () => {
    const { socketsConnected } = this.state;

    return {
      sockets: this.sockets,
      socketsConnected,
    };
  };

  render() {
    const { error, ErrorClass } = this.state;
    if (error) {
      throw new ErrorClass(error);
    }
    return <SocketContext.Provider value={this.getContext()} {...this.props} />;
  }
}

SocketProvider.defaultProps = {
  secure: true,
  sockets: {},
};

SocketProvider.propTypes = {
  secure: PropTypes.bool,
  sockets: PropTypes.object,
};

export const withSocket = () => (WrappedComponent) =>
  function (props) {
    return (
      <SocketContext.Consumer>
        {(context) => <WrappedComponent {...context} {...props} />}
      </SocketContext.Consumer>
    );
  };
